import beerIcon from "../assets/page_logo.png";
import { useNavigate } from "react-router-dom";
import React, { useEffect, useState, useRef } from "react";
import { toast } from "react-toastify";
import { authApi } from "../services/api";
import { useAuth } from "../contexts/AuthContext";
import VerifyEmailCta from "../components/VerifyEmailCta";

function AdminLogin() {
  const [showPassword, setShowPassword] = useState(false);
  const [forgotMode, setForgotMode] = useState(false);
  const [email, setEmail] = useState("");
  const [loginEmail, setLoginEmail] = useState(""); // Email for login form
  const [loginPassword, setLoginPassword] = useState(""); // Password for login form
  const [isValid, setIsValid] = useState(false);
  const [otpMode, setOtpMode] = useState(false);
  const [resetMode, setResetMode] = useState(false);
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [otp, setOtp] = useState(["", "", "", "", "", ""]); // 6 empty strings for 6-digit OTP
  const [resendCooldown, setResendCooldown] = useState(true);
  const [loginLoading, setLoginLoading] = useState(false);
  const [loginError, setLoginError] = useState("");
  const inputRefs = useRef([]);
  const [ticket, setTicket] = useState("");
  const [cooldownTime, setCooldownTime] = useState(60);

  const { login } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    console.log(
      "🕒 [Frontend] Initial 60s cooldown started — 'Send again' disabled",
    );
    setResendCooldown(true);
    setCooldownTime(60);

    const interval = setInterval(() => {
      setCooldownTime((prev) => {
        if (prev <= 1) {
          clearInterval(interval);
          setResendCooldown(false);
          console.log("✅ [Frontend] Cooldown finished — resend enabled");
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    return () => clearInterval(interval);
  }, []);

  const handleEmailChange = (e) => {
    const value = e.target.value;
    setEmail(value);
    // setIsValid(/^[\w-\.]+@([\w-]+\.)+[\w-]{2,4}$/.test(value));
    setIsValid(/^[\w.+-]+@([\w-]+\.)+[\w-]{2,}$/i.test(value));
  };

  const getApiErrorMessage = (response, fallbackMessage) => {
    const detail = response?.details?.detail;
    const error = response?.error;
    if (typeof detail === "string" && detail.trim()) return detail;
    if (typeof error === "string" && error.trim()) {
      return error.replace(/_/g, " ");
    }
    return fallbackMessage;
  };

  const handleLogin = async () => {
    if (!loginEmail || !loginPassword) {
      toast.error("Please enter both email and password", {
        position: "top-right",
        autoClose: 3000,
      });
      setLoginError("Please enter both email and password");
      return;
    }

    setLoginLoading(true);
    setLoginError("");

    try {
      const result = await login({
        email: loginEmail,
        password: loginPassword,
      });

      if (result.success) {
        // Show success toast
        toast.success("Login successful! Redirecting...", {
          position: "top-right",
          autoClose: 2000,
        });

        // Small delay to show success message before navigation
        setTimeout(() => {
          navigate("/admin-dashboard");
        }, 500);
      } else {
        const errorMessage = result.error || "Login failed. Please try again.";
        toast.error(errorMessage, {
          position: "top-right",
          autoClose: 4000,
        });
        setLoginError(errorMessage);
      }
    } catch (error) {
      console.log("Login error:", error?.result?.error);
      const errorMessage = error.message || "An error occurred during login";
      toast.error(errorMessage, {
        position: "top-right",
        autoClose: 4000,
      });
      setLoginError(errorMessage);
    } finally {
      setLoginLoading(false);
    }
  };

  return (
    <div
      style={{
        backgroundColor: "#F9F4EA",
        width: "100%",
        // minHeight, not height, and no `overflow: hidden`. Together those two made anything
        // taller than the viewport unreachable — and because this row centres its content,
        // what did not fit spilled off BOTH edges with no way to scroll to either. On iOS the
        // collapsing URL bar makes 100vh taller than the visible area as well. AC-MOBILE-F2.
        minHeight: "100vh",
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
      }}
    >
      <div
        style={{
          display: "flex",
          // The 400px logo and the card beside it total ~950px. Wrapping puts them on
          // separate centred lines below that width, which is the stacked phone layout —
          // no breakpoint, no state, one property. AC-MOBILE-F2.
          flexWrap: "wrap",
          alignItems: "center",
          justifyContent: "center",
          width: "100%",
          maxWidth: "1200px",
          gap: "clamp(24px, 6vw, 80px)",
          padding: "20px",
          boxSizing: "border-box",
        }}
      >
        {/* Left: MyHoom + Beer Icon */}
        <div style={{ display: "flex", alignItems: "center", gap: "16px" }}>
          <img
            src={beerIcon}
            alt="beer icon"
            style={{ width: "min(400px, 60vw)", height: "auto", cursor: "pointer" }}
            onClick={() => {
              if (forgotMode && resetMode) {
                // 🔙 From Create New Password → back to OTP
                setResetMode(false);
              } else if (forgotMode && otpMode) {
                // 🔙 From OTP → back to Forgot Password (email)
                setOtpMode(false);
              } else if (forgotMode) {
                // 🔙 From Forgot Password → back to Login
                setForgotMode(false);
              } else {
                navigate("/");
              }
            }}
          />
        </div>

        {/* Right: Form */}
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
          }}
        >
          <div
            style={{
              // min(..., 100%) rather than the bare pixel value: each of these is wider
              // than a phone, and the card is a flex item that would otherwise force the
              // row past the viewport. AC-MOBILE-F2.
              width: forgotMode
                ? resetMode
                  ? "min(472px, 100%)" // 🟣 widest for "Create New Password"
                  : otpMode
                    ? "min(540px, 100%)" // 🟢 OTP screen
                    : "min(472px, 100%)" // 🟠 Forgot email screen
                : "min(432px, 100%)",

              backgroundColor: "white",
              borderRadius: "12px",
              boxShadow: "0 4px 15px rgba(0,0,0,0.1)",
              padding: "30px",
              display: "flex",
              flexDirection: "column",
              boxSizing: "border-box",
              transition: "all 0.3s ease",
            }}
          >
            {forgotMode ? (
              !otpMode ? (
                // 🟠 STEP 1: Forgot Password Screen
                <>
                  <h2
                    style={{
                      textAlign: "center",
                      marginBottom: "30px",
                      fontSize: "2rem",
                      fontWeight: "bold",
                      color: "black",
                    }}
                  >
                    Forgot your Password?
                  </h2>

                  <div style={{ marginBottom: "30px", position: "relative" }}>
                    <label style={{ fontSize: "14px", color: "black" }}>
                      Email Address<span style={{ color: "red" }}>*</span>
                    </label>

                    <input
                      type="email"
                      value={email}
                      onChange={handleEmailChange}
                      placeholder="operator@gmail.com"
                      style={{
                        width: "100%",
                        padding: "12px",
                        borderRadius: "8px",
                        border: "1px solid #ccc",
                        marginTop: "5px",
                        boxSizing: "border-box",
                      }}
                    />

                    {isValid && (
                      <span
                        style={{
                          position: "absolute",
                          right: "12px",
                          top: "65%",
                          transform: "translateY(-40%)",
                          fontSize: "14px",
                        }}
                      >
                        <img
                          src={require("../assets/check.png")}
                          alt="valid"
                          style={{ width: "14px", height: "14px" }}
                        />
                      </span>
                    )}
                  </div>

                  <button
                    onClick={async (e) => {
                      e.preventDefault();

                      if (!email) {
                        toast.error("Please enter your email address.", {
                          position: "top-right",
                          autoClose: 3000,
                        });
                        return;
                      }

                      // 🪲 DEBUG LOG: Show what is being sent
                      const requestBody = {
                        username: email,
                        email: email,
                      };
                      console.log(
                        "🟢 [Forgot Password] Sending request via proxy:",
                        requestBody,
                      );

                      try {
                        const response = await authApi.forgotPassword(requestBody);

                        // Some backend responses return HTTP 200 with an error payload.
                        if (response?.error) {
                          throw new Error(
                            getApiErrorMessage(
                              response,
                              "Failed to send OTP. Please check your email.",
                            ),
                          );
                        }

                        toast.success("OTP sent to your email!", {
                          position: "top-right",
                          autoClose: 3000,
                        });
                        console.log(
                          "✅ [Forgot Password] OTP sent successfully",
                        );
                        setOtpMode(true);
                      } catch (err) {
                        console.error("🚨 [Forgot Password] Error:", err);
                        toast.error(err.message || "Failed to send OTP.", {
                          position: "top-right",
                          autoClose: 4000,
                        });
                      }
                    }}
                    style={{
                      width: "100%",
                      padding: "14px",
                      borderRadius: "30px",
                      border: "none",
                      cursor: "pointer",
                      background: "#DE810E",
                      color: "white",
                      fontWeight: "bold",
                      fontSize: "1rem",
                    }}
                  >
                    Reset Password
                  </button>
                </>
              ) : !resetMode ? (
                <div
                  style={{ width: "100%", fontFamily: "Poppins, sans-serif" }}
                >
                  {/* 1️⃣ Title */}
                  <h2
                    style={{
                      textAlign: "center",
                      width: "100%",
                      fontSize: "1.8rem",
                      fontWeight: "bold",
                      color: "#1E344D",
                      marginBottom: "16px",
                    }}
                  >
                    Admin Panel
                  </h2>

                  {/* 2️⃣ Subtitle + description */}
                  <div style={{ marginBottom: "16px" }}>
                    <h3
                      style={{
                        fontSize: "32px",
                        fontWeight: "bold",
                        color: "#1E344D",
                        marginBottom: "16px",
                      }}
                    >
                      Enter your OTP
                    </h3>
                    <p
                      style={{
                        fontSize: "14px",
                        color: "black",
                        lineHeight: "1.6",
                      }}
                    >
                      Enter the 6-digit code we just sent to{" "}
                      <span style={{ fontWeight: "600" }}>{email}</span>{" "}
                      <span
                        style={{
                          marginTop: "15px",
                          fontWeight: "bold",
                          color: "#DE810E",
                          cursor: "pointer",
                          marginLeft: "6px",
                        }}
                        onClick={() => setOtpMode(false)}
                      >
                        Edit
                      </span>
                    </p>
                    {/* <p
                      style={{
                        fontSize: "14px",
                        color: "black",
                        lineHeight: "1.6",
                      }}
                    >
                      Prefer to receive it via text?{" "}
                      <span
                        style={{
                          marginTop: "15px",
                          fontWeight: "bold",
                          color: "#DE810E",
                          cursor: "pointer",
                          marginLeft: "3px",
                        }}
                        onClick={() => setOtpMode(false)}
                      >
                        Receive via SMS
                      </span>
                    </p> */}
                  </div>

                  {/* 3️⃣ OTP Inputs */}
                  <div
                    style={{
                      display: "flex",
                      justifyContent: "center",
                      // Same geometry as the signup OTP row, and the same reason —
                      // AC-MOBILE-F11. Six 52px boxes plus five 17px gaps need 397px
                      // against a 330px card content box, so the row pushed the card
                      // past both screen edges and the first box landed at x=-3, where
                      // nothing can scroll to it. Both values clamp down together so the
                      // row still fits on one line, and hold their exact desktop
                      // measurements above ~450px.
                      gap: "clamp(6px, 2vw, 17px)",
                      marginBottom: "16px",
                    }}
                  >
                    {otp.map((digit, i) => (
                      <input
                        key={i}
                        maxLength={1}
                        value={digit}
                        ref={(el) => (inputRefs.current[i] = el)} // 👈 store refs for focusing
                        onChange={(e) => {
                          const val = e.target.value.replace(/\D/g, ""); // allow only numbers
                          const newOtp = [...otp];
                          newOtp[i] = val;
                          setOtp(newOtp);

                          // 👉 Auto move to next input
                          if (val && i < otp.length - 1) {
                            inputRefs.current[i + 1]?.focus();
                          }
                        }}
                        onKeyDown={(e) => {
                          // 👈 Handle backspace to move backward
                          if (e.key === "Backspace" && !otp[i] && i > 0) {
                            inputRefs.current[i - 1]?.focus();
                          }
                        }}
                        style={{
                          width: "clamp(36px, 11.5vw, 52px)",
                          height: "clamp(36px, 11.5vw, 52px)",
                          flexShrink: 0,
                          borderRadius: "8px",
                          backgroundColor: "rgba(30, 52, 77, 0.1)",
                          border: "none",
                          textAlign: "center",
                          fontSize: "20px",
                          fontWeight: "bold",
                          color: "#1E344D",
                          outline: "none",
                          marginBottom: "40px",
                          marginTop: "40px",
                        }}
                      />
                    ))}
                  </div>

                  {/* 4️⃣ Verify button */}
                  <button
                    onClick={async () => {
                      const code = otp.join("");
                      if (code.length < 6) {
                        toast.error("Please enter full 6-digit OTP.", {
                          position: "top-right",
                          autoClose: 3000,
                        });
                        return;
                      }

                      try {
                        const data = await authApi.verifyOTP({
                          email,
                          code,
                          purpose: "forgot_password",
                        });
                        setTicket(data.next.ticket);
                        toast.success("OTP verified successfully!", {
                          position: "top-right",
                          autoClose: 3000,
                        });
                        setResetMode(true);
                      } catch (err) {
                        console.error("🚨 [OTP Verify] Error:", err);
                        toast.error(err.message || "Invalid OTP. Try again.", {
                          position: "top-right",
                          autoClose: 4000,
                        });
                      }
                    }}
                    style={{
                      width: "100%",
                      padding: "14px",
                      borderRadius: "9999px",
                      border: "none",
                      cursor: "pointer",
                      background: "#DE810E",
                      color: "white",
                      fontWeight: "bold",
                      fontSize: "1rem",
                      marginBottom: "40px",
                    }}
                  >
                    Verify
                  </button>

                  {/* Resend OTP */}
                  <p
                    style={{
                      fontSize: "14px",
                      color: "#1E344D",
                      textAlign: "center",
                      marginBottom: "0",
                    }}
                  >
                    Didn’t get a code?{" "}
                    <span
                      style={{
                        fontWeight: "bold",
                        color: "#DE810E",
                        cursor: resendCooldown ? "not-allowed" : "pointer",
                        opacity: resendCooldown ? 0.6 : 1,
                        pointerEvents: resendCooldown ? "none" : "auto",
                        transition: "opacity 0.3s ease",
                      }}
                      onClick={async () => {
                        if (resendCooldown) return;

                        try {
                          console.log(
                            "🟡 [Frontend] Sending resend OTP request...",
                          );
                          const response = await authApi.resendOTP({
                            username: email,
                            email,
                          });

                          if (response?.error) {
                            throw new Error(
                              getApiErrorMessage(
                                response,
                                "Failed to resend OTP. Please check your email.",
                              ),
                            );
                          }

                          toast.success("OTP resent successfully!", {
                            position: "top-right",
                            autoClose: 3000,
                          });
                          console.log(
                            "🕒 [Frontend] Restarting 60s cooldown after resend",
                          );

                          setResendCooldown(true);
                          setCooldownTime(60);

                          const interval = setInterval(() => {
                            setCooldownTime((prev) => {
                              if (prev <= 1) {
                                clearInterval(interval);
                                setResendCooldown(false);
                                console.log(
                                  "✅ [Frontend] Cooldown finished — resend enabled",
                                );
                                return 0;
                              }
                              return prev - 1;
                            });
                          }, 1000);
                        } catch (err) {
                          console.error("🚨 [Frontend] Error:", err);
                          toast.error(err.message || "Failed to resend OTP.", {
                            position: "top-right",
                            autoClose: 4000,
                          });
                        }
                      }}
                    >
                      {resendCooldown
                        ? `Send again in ${cooldownTime}s`
                        : "Send again"}
                    </span>
                  </p>
                </div>
              ) : (
                // 🔵 STEP 3: Create New Password Screen
                <div
                  style={{ width: "100%", fontFamily: "Poppins, sans-serif" }}
                >
                  <h2
                    style={{
                      textAlign: "center",
                      width: "90%",
                      fontSize: "24px",
                      fontWeight: "bold",
                      color: "#1E344D",
                      marginBottom: "40px",
                    }}
                  >
                    Admin Panel
                  </h2>

                  <h3
                    style={{
                      fontSize: "32px",
                      fontWeight: "bold",
                      color: "#1E344D",
                      textAlign: "left",
                      marginBottom: "40px",
                    }}
                  >
                    Create New Password
                  </h3>

                  {/* ✅ Calculate password rules outside JSX IIFE */}
                  {(() => {
                    const criteria = {
                      upper: /[A-Z]/.test(newPassword),
                      number: /\d/.test(newPassword),
                      lower: /[a-z]/.test(newPassword),
                      special: /[!@#^*_?{}-]/.test(newPassword),
                      length: newPassword.length >= 8,
                    };

                    const allSatisfied = Object.values(criteria).every(Boolean);
                    const passwordsMatch =
                      confirmPassword.length > 0 &&
                      newPassword === confirmPassword;
                    const canSave = allSatisfied && passwordsMatch; // ✅ depends only on password

                    const rules = [
                      {
                        label: "At least one uppercase letter",
                        satisfied: criteria.upper,
                      },
                      {
                        label: "At least one numeral (0-9)",
                        satisfied: criteria.number,
                      },
                      {
                        label: "At least one lowercase letter",
                        satisfied: criteria.lower,
                      },
                      {
                        label: "At least one special symbol (!@#^*_?{}-)",
                        satisfied: criteria.special,
                      },
                      {
                        label: "Minimum of 8 characters",
                        satisfied: criteria.length,
                      },
                    ];

                    return (
                      <>
                        {/* Password Input */}
                        <div
                          style={{
                            position: "relative",
                            width: "100%",
                            marginBottom: "24px",
                          }}
                        >
                          <input
                            type="password"
                            value={newPassword}
                            onChange={(e) => setNewPassword(e.target.value)}
                            placeholder="Enter new password"
                            style={{
                              width: "100%",
                              height: "56px",
                              padding: "12px",
                              borderRadius: "4px",
                              border: "1px solid #ccc",
                              marginTop: "5px",
                              boxSizing: "border-box",
                            }}
                          />
                          {allSatisfied && (
                            <span
                              style={{
                                position: "absolute",
                                top: "50%",
                                transform: "translateX(-190%) translateY(-30%)",
                              }}
                            >
                              <img
                                src={require("../assets/check.png")}
                                alt="valid"
                                style={{ width: "16px", height: "16px" }}
                              />
                            </span>
                          )}
                        </div>

                        {/* Rules */}
                        <ul
                          style={{
                            listStyle: "none",
                            padding: 0,
                            marginBottom: "24px",
                          }}
                        >
                          {rules.map((rule, i) => (
                            <li
                              key={i}
                              style={{
                                display: "flex",
                                alignItems: "center",
                                marginBottom: "8px",
                                color: rule.satisfied
                                  ? "#1E344D"
                                  : "rgba(30, 52, 77, 0.3)",
                                fontSize: "14px",
                                transition: "color 0.3s ease",
                              }}
                            >
                              <img
                                src={require("../assets/check.png")}
                                alt="check"
                                style={{
                                  width: "14px",
                                  height: "14px",
                                  marginRight: "8px",
                                  opacity: rule.satisfied ? 1 : 0.3,
                                  transition: "opacity 0.3s ease",
                                }}
                              />
                              {rule.label}
                            </li>
                          ))}
                        </ul>

                        {/* Confirm Password */}
                        <label
                          style={{
                            marginTop: "24px",
                            fontSize: "14px",
                            color: "black",
                          }}
                        >
                          Confirm New Password
                          <span style={{ color: "red" }}>*</span>
                        </label>
                        <input
                          type="password"
                          value={confirmPassword}
                          onChange={(e) => setConfirmPassword(e.target.value)}
                          placeholder="Re-enter new password"
                          style={{
                            width: "100%",
                            height: "56px",
                            padding: "12px",
                            borderRadius: "4px",
                            border: "1px solid #ccc",
                            marginTop: "5px",
                            boxSizing: "border-box",
                            marginBottom: "40px",
                          }}
                        />

                        {/* Save Button */}
                        <button
                          onClick={async () => {
                            if (!canSave) {
                              toast.error("Please meet all requirements.", {
                                position: "top-right",
                                autoClose: 3000,
                              });
                              return;
                            }
                            try {
                              await authApi.changePassword({
                                ticket,
                                new_password: newPassword,
                              });
                              toast.success("Password successfully reset!", {
                                position: "top-right",
                                autoClose: 3000,
                              });
                              setForgotMode(false);
                              setOtpMode(false);
                              setResetMode(false);
                              setEmail("");
                              setNewPassword("");
                              setConfirmPassword("");
                            } catch (err) {
                              console.error("🚨 [Reset Password] Error:", err);
                              toast.error(
                                err.message || "Failed to reset password.",
                                {
                                  position: "top-right",
                                  autoClose: 4000,
                                },
                              );
                            }
                          }}
                          style={{
                            marginTop: "40px",
                            width: "100%",
                            height: "56px",
                            padding: "14px",
                            borderRadius: "9999px",
                            border: "none",
                            cursor: canSave ? "pointer" : "not-allowed",
                            background: canSave
                              ? "#DE810E"
                              : "rgba(222, 129, 14, 0.4)",
                            color: "white",
                            fontWeight: "bold",
                            fontSize: "1rem",
                            transition: "all 0.3s ease",
                          }}
                          disabled={!canSave}
                        >
                          Save Password
                        </button>
                      </>
                    );
                  })()}
                </div>
              )
            ) : (
              /* LOGIN MODE */
              <>
                <h2
                  style={{
                    textAlign: "center",
                    marginBottom: "30px",
                    fontSize: "2rem",
                    fontWeight: "bold",
                    color: "black",
                  }}
                >
                  Admin Panel
                </h2>

                {/* Email */}
                <div style={{ marginBottom: "20px" }}>
                  <label
                    style={{
                      fontSize: "14px",
                      fontWeight: "bold",
                      color: "black",
                    }}
                  >
                    Client Email
                  </label>
                  <input
                    type="email"
                    value={loginEmail}
                    onChange={(e) => setLoginEmail(e.target.value)}
                    placeholder="operator@gmail.com"
                    style={{
                      width: "100%",
                      padding: "12px",
                      borderRadius: "8px",
                      border: "1px solid #ccc",
                      marginTop: "5px",
                      boxSizing: "border-box",
                    }}
                  />
                </div>

                {/* Password */}
                <div style={{ position: "relative" }}>
                  <label
                    style={{
                      fontSize: "14px",
                      fontWeight: "bold",
                      color: "black",
                    }}
                  >
                    Password
                  </label>
                  <input
                    type={showPassword ? "text" : "password"}
                    value={loginPassword}
                    onChange={(e) => setLoginPassword(e.target.value)}
                    placeholder="********"
                    onKeyPress={(e) => {
                      if (e.key === "Enter") {
                        handleLogin();
                      }
                    }}
                    style={{
                      width: "100%",
                      padding: "12px",
                      borderRadius: "8px",
                      border: "1px solid #ccc",
                      marginTop: "5px",
                      boxSizing: "border-box",
                      paddingRight: "40px",
                    }}
                  />
                  <span
                    onClick={() => setShowPassword(!showPassword)}
                    style={{
                      position: "absolute",
                      right: "12px",
                      top: "50%", // ✅ center vertically
                      transform: "translateY(-0%)",
                      cursor: "pointer",
                    }}
                  >
                    {showPassword ? (
                      <img
                        src="https://cdn.jsdelivr.net/npm/bootstrap-icons/icons/eye-fill.svg"
                        alt="Hide"
                        style={{ width: "20px", height: "20px" }}
                      />
                    ) : (
                      <img
                        src="https://cdn.jsdelivr.net/npm/bootstrap-icons/icons/eye-slash-fill.svg"
                        alt="Show"
                        style={{ width: "20px", height: "20px" }}
                      />
                    )}
                  </span>
                </div>

                {/* Account-recovery links. Verification sits beside the password reset
                    because the two are easily confused from the outside — "I can't get in"
                    has these two causes — and because a partner stuck at the OTP step
                    should not have to fail a sign-in first to discover the way out. Weighted
                    the same as "Forgot Password?" — they are peer recovery routes, and
                    styling one as secondary made it read as a footnote. The high-emphasis
                    button version appears in the error state, when we know it is the fix. */}
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    gap: "12px",
                    margin: "40px 0",
                  }}
                >
                  <span
                    style={{
                      fontSize: "16px",
                      color: "black",
                      cursor: "pointer",
                      fontWeight: "bold",
                    }}
                    onClick={() => setForgotMode(true)}
                    onMouseEnter={(e) =>
                      (e.currentTarget.style.textDecoration = "underline")
                    }
                    onMouseLeave={(e) =>
                      (e.currentTarget.style.textDecoration = "none")
                    }
                  >
                    Forgot Password?
                  </span>

                  <span aria-hidden="true" style={{ color: "#ccc" }}>
                    |
                  </span>

                  <span
                    style={{
                      fontSize: "16px",
                      color: "black",
                      cursor: "pointer",
                      fontWeight: "bold",
                    }}
                    onClick={() => navigate("/verify-email")}
                    onMouseEnter={(e) =>
                      (e.currentTarget.style.textDecoration = "underline")
                    }
                    onMouseLeave={(e) =>
                      (e.currentTarget.style.textDecoration = "none")
                    }
                  >
                    Verify Email
                  </span>
                </div>

                {/* Error Message */}
                {loginError && (
                  <div
                    style={{
                      padding: "10px",
                      marginBottom: "15px",
                      borderRadius: "8px",
                      backgroundColor: "#fee",
                      color: "#c33",
                      fontSize: "14px",
                      textAlign: "center",
                    }}
                  >
                    {loginError}
                    {/* login_user maps both an unconfirmed Cognito user and an inactive
                        profile onto this one string (services/auth.py), and it's the dead end
                        a partner who skipped the OTP always lands on. Offer the way out. */}
                    {/not confirmed/i.test(loginError) && (
                      <div style={{ marginTop: "12px" }}>
                        <p
                          style={{
                            margin: "0 0 10px",
                            color: "#8a5300",
                            fontSize: "13px",
                            lineHeight: 1.4,
                          }}
                        >
                          Your email was never verified, so this account isn't active
                          yet. Finish it with a new code — you'll set your password at
                          the same time.
                        </p>
                        <VerifyEmailCta email={loginEmail} />
                      </div>
                    )}
                  </div>
                )}

                {/* Login */}
                <button
                  style={{
                    width: "100%",
                    padding: "14px",
                    borderRadius: "30px",
                    border: "none",
                    cursor: loginLoading ? "not-allowed" : "pointer",
                    background: loginLoading ? "#ccc" : "#DE810E",
                    color: "white",
                    fontWeight: "bold",
                    fontSize: "1rem",
                    marginBottom: "15px",
                    opacity: loginLoading ? 0.7 : 1,
                  }}
                  onClick={handleLogin}
                  disabled={loginLoading}
                >
                  {loginLoading ? "Logging in..." : "Log In"}
                </button>

                {/* Signup */}
                <p
                  style={{
                    textAlign: "center",
                    fontSize: "16px",
                    color: "black",
                  }}
                >
                  Not yet a member?{" "}
                  <span
                    style={{
                      fontWeight: "bold",
                      cursor: "pointer",
                      color: "black",
                    }}
                    onClick={() => navigate("/admin-signup")} // ✅ navigate to signup page
                  >
                    Sign up
                  </span>
                </p>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

export default AdminLogin;
